import React, { useState, useEffect, useRef } from "react";
import { 
  Store, 
  Users, 
  BarChart3, 
  CreditCard, 
  HeartHandshake, 
  Zap,
  ArrowRight
} from "lucide-react";
import { motion, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";

function AnimatedNumber({ value, prefix = "", suffix = "", duration = 1.6 }) {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  useEffect(() => {
    if (!isInView) return;
    let startTime;
    let animationFrame;

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      // Ease out expo for lively count-up
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setDisplayValue(Math.floor(easeProgress * value));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(step);
      } else {
        setDisplayValue(value);
      }
    };

    animationFrame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationFrame);
  }, [isInView, value, duration]);

  return (
    <span ref={ref}>
      {prefix}
      {displayValue.toLocaleString()}
      {suffix}
    </span>
  );
}

export default function VendorSection() {
  const navigate = useNavigate();

  return (
    <section id="vendors" className="py-20 sm:py-28 relative bg-white border-t border-slate-50/50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Content */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-[11px] font-bold uppercase tracking-widest text-primary mb-3 block">
            EMPOWERING AUTOMOTIVE PROFESSIONALS
          </span>
          <h2 className="text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-4">
            Grow Your Automotive Business
          </h2>
          <p className="text-base text-slate-600 font-medium leading-relaxed">
            Connect with high-intent customers and streamline your operations with our comprehensive business platform and portal.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          
          {/* Overview Graph Card (Left 2/3) */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-2 bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] flex flex-col justify-between hover:shadow-[0_8px_40px_rgba(0,122,255,0.08)] transition-shadow"
          >
            <div className="flex justify-between items-start mb-8">
              <h3 className="text-lg font-bold text-slate-900">Overview</h3>
              <select className="text-xs font-medium text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-2 py-1 outline-none">
                <option>This Month</option>
                <option>Last Month</option>
              </select>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-10">
              <div>
                <p className="text-xs text-slate-500 font-medium mb-1">Total Revenue</p>
                <h4 className="text-2xl sm:text-3xl font-black text-slate-900 font-mono tracking-tight">
                  <AnimatedNumber value={78540} prefix="$" />
                </h4>
                <span className="text-[10px] text-emerald-600 font-bold inline-flex items-center gap-1 mt-1 tracking-wide bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100 animate-sweep-shine">
                  <span className="animate-growth-shine text-[9px]">▲</span> 12.6% vs last month
                </span>
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium mb-1">Bookings</p>
                <h4 className="text-2xl sm:text-3xl font-black text-slate-900 font-mono tracking-tight">
                  <AnimatedNumber value={248} />
                </h4>
                <span className="text-[10px] text-emerald-600 font-bold inline-flex items-center gap-1 mt-1 tracking-wide bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100 animate-sweep-shine">
                  <span className="animate-growth-shine text-[9px]">▲</span> 18.2% vs last month
                </span>
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium mb-1">Customers</p>
                <h4 className="text-2xl sm:text-3xl font-black text-slate-900 font-mono tracking-tight">
                  <AnimatedNumber value={189} />
                </h4>
                <span className="text-[10px] text-emerald-600 font-bold inline-flex items-center gap-1 mt-1 tracking-wide bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100 animate-sweep-shine">
                  <span className="animate-growth-shine text-[9px]">▲</span> 22.1% vs last month
                </span>
              </div>
            </div>


            {/* Animated SVG Graph Mockup */}
            <div className="relative h-36 w-full mt-auto flex items-end">
              <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="vendorBlueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#007AFF" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#007AFF" stopOpacity="0.0" />
                  </linearGradient>
                </defs>
                {/* Gradient area underneath that fades in */}
                <motion.path 
                  d="M0,100 L0,80 Q20,90 40,60 T80,40 T100,20 L100,100 Z" 
                  fill="url(#vendorBlueGrad)"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 1.2, delay: 0.4 }}
                />
                {/* Self-drawing spline stroke */}
                <motion.path 
                  d="M0,80 Q20,90 40,60 T80,40 T100,20" 
                  fill="none" 
                  stroke="#007AFF" 
                  strokeWidth="2.5" 
                  vectorEffect="non-scaling-stroke"
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
                />
              </svg>
              {/* X-axis labels */}
              <div className="absolute bottom-0 left-0 w-full flex justify-between text-[10px] text-slate-400 font-medium translate-y-6">
                <span>May 1</span>
                <span>May 8</span>
                <span>May 15</span>
                <span>May 22</span>
                <span>May 29</span>
              </div>
            </div>
          </motion.div>

          {/* Right Stack (1/3) */}
          <div className="grid grid-rows-2 gap-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_40px_rgba(0,122,255,0.08)] transition-all flex flex-col group cursor-default"
            >
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center text-emerald-500 mb-4 group-hover:scale-110 transition-transform">
                <Store className="w-5 h-5" />
              </div>
              <h3 className="text-sm font-bold text-slate-900 mb-2 group-hover:text-primary transition-colors">Service Marketplace</h3>
              <p className="text-xs text-slate-500 font-medium leading-relaxed mb-4">List your business and directly connect with high-intent drivers seeking maintenance and repairs.</p>
              <button className="text-primary text-xs font-bold flex items-center gap-1 hover:underline mt-auto w-fit">
                Learn More <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_40px_rgba(0,122,255,0.08)] transition-all flex flex-col group cursor-default"
            >
              <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center text-indigo-500 mb-4 group-hover:scale-110 transition-transform">
                <Users className="w-5 h-5" />
              </div>
              <h3 className="text-sm font-bold text-slate-900 mb-2 group-hover:text-primary transition-colors">Team Management</h3>
              <p className="text-xs text-slate-500 font-medium leading-relaxed mb-4">Seamlessly manage staff schedules, track performance, and coordinate multiple locations.</p>
              <button className="text-primary text-xs font-bold flex items-center gap-1 hover:underline mt-auto w-fit">
                Learn More <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          </div>

          {/* Middle Row (3 cards) */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
            className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_40px_rgba(0,122,255,0.08)] transition-all flex flex-col group cursor-default"
          >
             <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-primary mb-4 group-hover:scale-110 transition-transform">
                <BarChart3 className="w-5 h-5" />
              </div>
              <h3 className="text-sm font-bold text-slate-900 mb-2 group-hover:text-primary transition-colors">Business Analytics</h3>
              <p className="text-xs text-slate-500 font-medium leading-relaxed mb-4">Access deep insights into revenue, booking rates, customer lifetime value, and overall growth.</p>
              <button className="text-primary text-xs font-bold flex items-center gap-1 hover:underline mt-auto w-fit">
                Learn More <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </button>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
            className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_40px_rgba(0,122,255,0.08)] transition-all flex flex-col group cursor-default"
          >
             <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center text-orange-500 mb-4 group-hover:scale-110 transition-transform">
                <CreditCard className="w-5 h-5" />
              </div>
              <h3 className="text-sm font-bold text-slate-900 mb-2 group-hover:text-primary transition-colors">Payment Processing</h3>
              <p className="text-xs text-slate-500 font-medium leading-relaxed mb-4">Secure payouts, automated invoicing, and comprehensive financial reporting built right in.</p>
              <button className="text-primary text-xs font-bold flex items-center gap-1 hover:underline mt-auto w-fit">
                Learn More <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </button>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
            className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_40px_rgba(0,122,255,0.08)] transition-all flex flex-col group cursor-default"
          >
             <div className="w-10 h-10 bg-rose-50 rounded-lg flex items-center justify-center text-rose-500 mb-4 group-hover:scale-110 transition-transform">
                <HeartHandshake className="w-5 h-5" />
              </div>
              <h3 className="text-sm font-bold text-slate-900 mb-2 group-hover:text-primary transition-colors">Customer Tools</h3>
              <p className="text-xs text-slate-500 font-medium leading-relaxed mb-4">Built-in CRM for automated follow-ups, personalized service reminders, and customer loyalty.</p>
              <button className="text-primary text-xs font-bold flex items-center gap-1 hover:underline mt-auto w-fit">
                Learn More <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </button>
          </motion.div>


          {/* Bottom Banner Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="lg:col-span-3 bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] flex flex-col md:flex-row items-center justify-between"
          >
             <div className="flex items-center gap-4 text-center md:text-left mb-6 md:mb-0">
                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center text-amber-500 shrink-0 mx-auto md:mx-0">
                  <Zap className="w-5 h-5 fill-current" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 mb-1">Smart Automation & Scheduling</h3>
                  <p className="text-xs text-slate-500 font-medium">AI-powered appointment management and automated communication systems to boost efficiency.</p>
                </div>
             </div>
             
             <div className="flex items-center gap-6 w-full md:w-auto">
               <button className="text-primary text-xs font-bold flex items-center gap-1 hover:underline mx-auto md:mx-0">
                  Learn More <ArrowRight className="w-3 h-3" />
                </button>
               <button 
                  onClick={() => navigate("/dashboard")}
                  className="bg-primary hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg font-bold text-sm shadow-md whitespace-nowrap w-full md:w-auto transition-colors"
                >
                 Access Business Portal
               </button>
             </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
